import { Link } from 'react-router'
import { LuArrowRight } from 'react-icons/lu'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { primaryLink, textLink } from '@/components/ui/linkStyles'
import { getServiceEnquiryPath, type ServiceOffer } from '@/data/pricing'
import { cn } from '@/lib/cn'

type ServicePricingProps = {
  title: string
  accent: string
  description: string
  offers: readonly ServiceOffer[]
  showDetails?: boolean
}

export const ServicePricing = ({
  title,
  accent,
  description,
  offers,
  showDetails = false,
}: ServicePricingProps) => {
  const singleOffer = offers.length === 1

  return (
    <section id="service-pricing" className="bg-base-50 py-16 sm:py-20 lg:py-24 scroll-mt-28">
      <div className="mx-auto max-w-6xl px-6">
        <p className="mb-4 flex items-center gap-3 text-xs font-medium uppercase tracking-[0.18em] text-green-700">
          <span className="h-px w-8 bg-gold-400" aria-hidden="true" />
          Services &amp; pricing
        </p>
        <SectionHeader title={title} accent={accent} description={description} />

        <div className={cn('grid gap-6', !singleOffer && 'md:grid-cols-2')}>
          {offers.map((offer) => (
            <article
              key={offer.id}
              aria-labelledby={`${offer.id}-title`}
              className="relative flex flex-col overflow-hidden rounded-3xl border border-base-200 bg-white"
            >
              <div className="h-1 w-full bg-green-700" aria-hidden="true" />
              <div
                className={cn(
                  'flex flex-1 flex-col p-7 sm:p-9',
                  singleOffer && 'lg:grid lg:grid-cols-[1.3fr_1fr] lg:gap-14 lg:p-12',
                )}
              >
                <div className="flex-1">
                  <span className="mb-6 inline-flex rounded-full border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-medium text-green-800">
                    {offer.category}
                  </span>
                  <h3
                    id={`${offer.id}-title`}
                    className="mb-4 font-display text-2xl leading-tight text-base-900 sm:text-3xl"
                  >
                    {offer.title}
                  </h3>
                  <p className="mb-8 max-w-lg text-base leading-7 text-base-600">
                    {offer.description}
                  </p>
                </div>

                <div
                  className={cn(
                    'border-t border-base-200 pt-6',
                    singleOffer && 'lg:border-t-0 lg:border-l lg:pl-12 lg:pt-0',
                  )}
                >
                  <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-display text-5xl tracking-tight text-green-900 sm:text-6xl">
                      {offer.price}
                    </span>
                    <span className="text-xs tracking-widest text-base-600">GBP</span>
                  </p>
                  <p className="mt-3 mb-7 text-sm leading-6 text-base-600">{offer.priceNote}</p>
                  <Link
                    to={getServiceEnquiryPath(offer)}
                    aria-label={`Enquire about ${offer.title}`}
                    className={cn(primaryLink, 'w-full')}
                  >
                    Book Now
                    <LuArrowRight aria-hidden="true" className="h-4 w-4 shrink-0" />
                  </Link>
                  {showDetails && (
                    <Link
                      to={offer.path}
                      aria-label={`Explore ${offer.title}`}
                      className={cn(textLink, 'mt-2 w-full justify-center')}
                    >
                      Explore this service
                    </Link>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-7 flex flex-col gap-2 text-sm leading-6 text-base-600 sm:flex-row sm:items-start sm:justify-between sm:gap-8">
          <p>All prices are in British pounds (GBP).</p>
          <p className="sm:max-w-sm sm:text-right">
            Enquire to confirm availability and details with Kandake. No online payment is taken.
          </p>
        </div>
      </div>
    </section>
  )
}
